import React from 'react';
import StaffProfileTemplate, {
  ProfileSection,
  ProfileParagraphs,
  ProfileBullets,
  ProfilePublications,
  ProfileContactSection,
} from './StaffProfileTemplate';

const AviholiChishiProfile = () => {
  const person = {
    id: 'aviholi-chishi',
    name: 'Aviholi Chishi',
    honorific: 'Ms',
    title: 'Assistant Researcher',
    affiliation: 'The Highland Institute, Kohima, Nagaland',
    image: 'https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/5juooupc_Aviholi.jpeg',
    email: 'avi@highlandinstitute.org',
    category: 'Research Staff',
    testid: 'aviholi-chishi-profile',
  };

  return (
    <StaffProfileTemplate person={person}>
      {/* Intellectual Biography */}
      <ProfileSection title="Intellectual Biography">
        <ProfileParagraphs
          items={[
            'Aviholi Chishi is an Assistant Researcher at The Highland Institute. She holds a Master of Philosophy (MPhil) in English from Lovely Professional University, Punjab, where her interdisciplinary research focused on multiculturalism in postcolonial India. Her research interests include ethnographic research, oral and visual documentation, and archiving.',
            'She has been awarded the prestigious Chevening Scholarship, the UK government\'s flagship international scholarship programme, to pursue a Master\'s in Postcolonial Studies at University of Leeds for the 2026–27 academic year.',
          ]}
        />
      </ProfileSection>

      {/* Research Foci & Areas of Work */}
      <ProfileSection title="Research Foci & Areas of Work">
        <ProfileParagraphs
          items={[
            'Her research focuses on how culture, power, and identity are represented in literary and filmic texts.',
          ]}
        />
        <ProfileBullets
          items={[
            'Postcolonial literature',
            'Trauma',
            'Memory',
            'Necropolitics',
            'Indigenous studies',
            'Film studies',
            'Critical theory',
          ]}
        />
      </ProfileSection>

      {/* Highland Institute Projects */}
      <ProfileSection title="Highland Institute Projects">
        <ProfileParagraphs
          items={[
            'Currently, she is working on the Modern Endangered Archives Programme (MEAP) with the University of California, Los Angeles (UCLA), focusing on documenting and digitizing endangered historical photographs.',
            <>At the Institute, she is also part of the <em>Tasting Tomorrow</em> research project, which explores the intersections of food, culture, and climate change. Through workshops, the project explores the question of how traditional foods and recipes can adapt to changing climatic conditions.</>,
            <>She has also worked on the project <em>Oral Histories of Nagaland</em>, which explored the historical trauma resulting from the political conflicts of 20th-century Nagaland.</>,
          ]}
        />
      </ProfileSection>

      {/* Public Engagement, Teaching & Community Work */}
      <ProfileSection title="Public Engagement, Teaching & Community Work">
        <ProfileParagraphs
          items={[
            'She is the film coordinator of the Highland Film Club, where she engages with the community through monthly screenings in schools and colleges. To amplify Indigenous films, she was invited by the Tata Steel Foundation to participate in Samvaad: A Tribal Conclave in Jamshedpur in 2024.',
            <>She served as researcher for the documentary <em>Axone Ghili: Tasting Tomorrow</em>. The film explored Indigenous cuisine, including axone, a fermented soybean paste, the Sumi folklore surrounding its origin, and climate change. She has also directed the short film <em>Think Before You Throw</em>, produced in collaboration with the Kohima Municipal Council, focusing on the 3Rs (Reduce, Reuse, and Recycle).</>,
            'Additionally, in 2023, she presented a paper titled "Shackled with Death, Death-Worlds and Trauma: A Necropolitical Reading of Easterine Kire\'s Bitter Wormwood and A Respectable Woman" at the international conference Moments, Movements, and Meanderings: Re-examining \'Northeast\' India.',
            <>Furthermore, she has worked with Dr. Christian Poske on his successful <em>Naga Ancestral Voices</em> exhibition, which was based on a research project on recirculating historical sound recordings from Nagaland in collaboration with the Pitt Rivers Museum, Oxford (UK). In 2024, Chishi was part of the <em>Look Up</em> exhibition at The Highland Institute, which showcased Indigenous astronomy, cosmology, and natural history.</>,
            'Chishi has also contributed to various workshops, conferences, and seminars at The Highland Institute and continues to mentor interns at the Summer School and engages in editorial work.',
          ]}
        />
      </ProfileSection>

      {/* Selected Publications & Knowledge Outputs */}
      <ProfileSection title="Selected Publications & Knowledge Outputs">
        <ProfilePublications
          items={[
            {
              year: '2024',
              citation: (
                <>
                  'Highland Film Club: Connecting People and Cultures through Film' in <em>The Highlander Journal</em>, Vol. 3, Issue 2, pp. 142–145.
                </>
              ),
            },
            {
              year: '2018',
              citation: (
                <>
                  'Deterioration of Human Values: A Study of Rohinton Mistry's Family Matters' in <em>Thematics</em>, Vol. 8, Issue 1, pp. 227–237.
                </>
              ),
            },
            {
              year: 'Forthcoming',
              citation: (
                <>
                  'Shackled with Death, Death-Worlds and Trauma: A Necropolitical Reading of Easterine Kire's Bitter Wormwood and A Respectable Woman', submitted to a peer-reviewed journal for publication.
                </>
              ),
            },
          ]}
        />
      </ProfileSection>

      {/* Professional Roles */}
      <ProfileSection title="Professional Roles">
        <ProfileBullets
          items={[
            'Assistant Researcher',
            'Coordinator of The Highland Film Club',
            'Assistant Editor of the HIMALAYA journal',
          ]}
        />
      </ProfileSection>

      {/* Contact & Scholarly Infrastructure */}
      <ProfileContactSection
        email={person.email}
        role="Assistant Researcher, The Highland Institute; Coordinator, The Highland Film Club; Assistant Editor, HIMALAYA journal"
        testidPrefix="aviholi"
        scholarlyProfiles={[
          { label: 'Academia.edu', url: 'https://lovely-professional-university.academia.edu/AviholiChishi', testid: 'aviholi-academia-link' },
          { label: 'LinkedIn', url: 'https://www.linkedin.com/in/aviholi-chishi-902a673b9', testid: 'aviholi-linkedin-link' },
        ]}
      />
    </StaffProfileTemplate>
  );
};

export default AviholiChishiProfile;
